import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Home from "./common_page/Home";
import Login from "./common_page/Login";
import Register from "./common_page/Register";
import ForgotPassword from "./common_page/ForgotPass";
import ChangePassword from "./common_page/ChangePassword";
import { HeaderFooterLayout } from "./common_components/Layout";

import AlliedLandingPage from "./modules/allied/page/AlliedLandingPage";
import AlliedApplicationDashboard from "./modules/allied/page/ApplicationsDashboard";
import AlliedApplicationDetail from "./modules/allied/page/ApplicationDetail";
import MyAlliedApplications from "./modules/allied/page/MyApplications";
import AlliedApplicationForm from "./modules/allied/page/AlliedApplicationForm";
import AlliedReviewApplication from "./modules/allied/page/AlliedReviewApplication";

// Faculty
import FacultyLandingPage from "./modules/faculty/page/FacultyLandingPage";
import MyFacultyApplications from "./modules/faculty/page/MyApplications";
import FacultyRecruitmentsList from "./modules/faculty/page/RecruitmentList";
import FacultyApplicationDashboard from "./modules/faculty/page/FacultyApplicationDashboard";

// Non-Faculty
import NonFacultyLandingPage from "./modules/non-faculty/page/NonFacultyLandingPage";
import MyNonFacultyApplications from "./modules/non-faculty/page/MyApplications";
import NonFacultyRecruitmentsList from "./modules/non-faculty/page/RecruitmentList";
import NonFacultyApplicationDashboard from "./modules/non-faculty/page/NonFacultyApplicationDashboard";

// Dynamic modules
import RecruitmentBuilder from "./modules/recruitment-builder/RecruitmentBuilder";
import ApplyApplication from "./modules/applications/ApplyApplication";
import ViewApplication from "./modules/applications/ViewApplication";
import PrintApplication from "./modules/applications/PrintApplication";
import ReviewApplication from "./modules/applications/ReviewApplication";

import NoticeManagement from "./modules/admin/NoticeManagement";
import AdmissionVacancyManagement from "./modules/allied/page/AdmissionVacancyManagement";
import MasterDataManagement from "./modules/admin/MasterDataManagement";
import UserManagement from "./modules/admin/UserManagement";

// Quarters Module Imports
import QuartersDashboard from "./modules/quarters/pages/QuartersDashboard";
import BuildingManagement from "./modules/quarters/pages/BuildingManagement";
import FlatManagement from "./modules/quarters/pages/FlatManagement";
import ReservationSettings from "./modules/quarters/pages/ReservationSettings";
import WeightSettings from "./modules/quarters/pages/WeightSettings";
import ApplicationForm from "./modules/quarters/pages/ApplicationForm";
import ApplicationList from "./modules/quarters/pages/ApplicationList";
import ApplicationDetail from "./modules/quarters/pages/ApplicationDetail";
import AllotmentEngine from "./modules/quarters/pages/AllotmentEngine";
import Reports from "./modules/quarters/pages/Reports";
import AuditLogs from "./modules/quarters/pages/AuditLogs";
import QuartersInventory from "./modules/quarters/pages/QuartersInventory";
import FlatDetail from "./modules/quarters/pages/FlatDetail";
import { UserRole } from "./types/status";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: (UserRole | string)[];
  disallowedRoles?: (UserRole | string)[];
}

/**
 * Universal ProtectedRoute guard.
 * Supports authentication enforcement and role-based permissions.
 * Super admins bypass allowedRoles checks.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  disallowedRoles,
}) => {
  const { isAuthenticated, user, isLoading, isSuperAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user) {
    console.log("Checking Role",allowedRoles,user.role)
    if (disallowedRoles && disallowedRoles.includes(user.role)) {
      return <Navigate to="/" replace />;
    }
    if (
      allowedRoles &&
      allowedRoles.length > 0 &&
      !isSuperAdmin &&
      !allowedRoles.includes(user.role)
    ) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};

function AppContent() {
  return (
    <>
      <Routes>
        <Route
          path="/faculty/application/:recruitmentId/:applicationId/print"
          element={
            <ProtectedRoute>
              <PrintApplication />
            </ProtectedRoute>
          }
        />
        <Route
          path="/faculty/application/:recruitmentId/:applicationId/view"
          element={
            <ProtectedRoute>
              <ViewApplication />
            </ProtectedRoute>
          }
        />
        <Route
          path="/non-faculty/application/:recruitmentId/:applicationId/print"
          element={
            <ProtectedRoute>
              <PrintApplication />
            </ProtectedRoute>
          }
        />
        <Route
          path="/non-faculty/application/:recruitmentId/:applicationId/view"
          element={
            <ProtectedRoute>
              <ViewApplication />
            </ProtectedRoute>
          }
        />
        <Route
          path="/allied/application/:applicationId/print"
          element={
            <ProtectedRoute>
              <AlliedApplicationDetail />
            </ProtectedRoute>
          }
        />
        {/* All other routes - inside HeaderFooterLayout */}
        <Route
          path="*"
          element={
            <HeaderFooterLayout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgotpassword" element={<ForgotPassword />} />
                <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />

                {/* Allied Routes */}
                <Route path="/allied">
                  <Route index element={<AlliedLandingPage />} />
                  <Route path="applications/all" element={<ProtectedRoute><MyAlliedApplications /></ProtectedRoute>} />
                  <Route path=":admissionId/applications" element={<ProtectedRoute><MyAlliedApplications /></ProtectedRoute>} />
                  <Route path="admission/:admissionId/applications" element={<ProtectedRoute><MyAlliedApplications /></ProtectedRoute>} />
                  <Route path="admission/:admissionId/apply" element={<ProtectedRoute><AlliedApplicationForm /></ProtectedRoute>} />
                  <Route path="admission/:admissionId/apply/:applicationId" element={<ProtectedRoute><AlliedApplicationForm /></ProtectedRoute>} />
                  <Route path="application/:applicationId/print" element={<ProtectedRoute><AlliedApplicationDetail /></ProtectedRoute>} />
                  <Route path="application/:applicationId/view" element={<ProtectedRoute><AlliedApplicationDetail /></ProtectedRoute>} />
                  <Route path="admin/admission-vacancies" element={<ProtectedRoute allowedRoles={[UserRole.STUDENT_ADMIN]}><AdmissionVacancyManagement /></ProtectedRoute>} />
                  <Route path="admin/:admissionId/applications" element={<ProtectedRoute allowedRoles={[UserRole.STUDENT_ADMIN]}><AlliedApplicationDashboard /></ProtectedRoute>} />
                  <Route path="admin/:admissionId/applications/all" element={<ProtectedRoute allowedRoles={[UserRole.STUDENT_ADMIN]}><AlliedApplicationDashboard /></ProtectedRoute>} />
                  <Route path="admin/:admissionId/applications/:applicationId" element={<ProtectedRoute allowedRoles={[UserRole.STUDENT_ADMIN]}><AlliedApplicationDetail /></ProtectedRoute>} />
                  <Route path="admin/application/:id/review" element={<ProtectedRoute allowedRoles={[UserRole.STUDENT_ADMIN]}><AlliedReviewApplication /></ProtectedRoute>} />
                </Route>

                {/* Faculty Routes */}
                <Route path="/faculty">
                  <Route index element={<FacultyLandingPage />} />
                  <Route path="application/:recruitmentId/apply" element={<ProtectedRoute><ApplyApplication /></ProtectedRoute>} />
                  <Route path="application/:recruitmentId/:applicationId/edit" element={<ProtectedRoute><ApplyApplication /></ProtectedRoute>} />
                  <Route path=":recruitmentId/applications" element={<ProtectedRoute><MyFacultyApplications /></ProtectedRoute>} />
                  <Route path="admin/recruitment/all" element={<ProtectedRoute allowedRoles={[UserRole.RECRUITMENT_ADMIN]}><FacultyRecruitmentsList /></ProtectedRoute>} />
                  <Route path="admin/recruitment/apply" element={<ProtectedRoute allowedRoles={[UserRole.RECRUITMENT_ADMIN]}><RecruitmentBuilder /></ProtectedRoute>} />
                  <Route path="admin/recruitment/:recruitmentId/edit" element={<ProtectedRoute allowedRoles={[UserRole.RECRUITMENT_ADMIN]}><RecruitmentBuilder /></ProtectedRoute>} />
                  <Route path="admin/:recruitmentId/applications/all" element={<ProtectedRoute allowedRoles={[UserRole.RECRUITMENT_ADMIN]}><FacultyApplicationDashboard /></ProtectedRoute>} />
                  <Route path="admin/application/:recruitmentId/:applicationId/review" element={<ProtectedRoute allowedRoles={[UserRole.RECRUITMENT_ADMIN]}><ReviewApplication /></ProtectedRoute>} />
                </Route>

                {/* Non-Faculty Routes */}
                <Route path="/non-faculty">
                  <Route index element={<NonFacultyLandingPage />} />
                  <Route path="application/:recruitmentId/apply" element={<ProtectedRoute><ApplyApplication /></ProtectedRoute>} />
                  <Route path="application/:recruitmentId/:applicationId/edit" element={<ProtectedRoute><ApplyApplication /></ProtectedRoute>} />
                  <Route path=":recruitmentId/applications" element={<ProtectedRoute><MyNonFacultyApplications /></ProtectedRoute>} />
                  <Route path="admin/recruitment/all" element={<ProtectedRoute allowedRoles={[UserRole.RECRUITMENT_ADMIN]}><NonFacultyRecruitmentsList /></ProtectedRoute>} />
                  <Route path="admin/recruitment/apply" element={<ProtectedRoute allowedRoles={[UserRole.RECRUITMENT_ADMIN]}><RecruitmentBuilder /></ProtectedRoute>} />
                  <Route path="admin/recruitment/:recruitmentId/edit" element={<ProtectedRoute allowedRoles={[UserRole.RECRUITMENT_ADMIN]}><RecruitmentBuilder /></ProtectedRoute>} />
                  <Route path="admin/:recruitmentId/applications/all" element={<ProtectedRoute allowedRoles={[UserRole.RECRUITMENT_ADMIN]}><NonFacultyApplicationDashboard /></ProtectedRoute>} />
                  <Route path="admin/application/:recruitmentId/:applicationId/review" element={<ProtectedRoute allowedRoles={[UserRole.RECRUITMENT_ADMIN]}><ReviewApplication /></ProtectedRoute>} />
                </Route>

                {/* Admin routes */}
                <Route path="/admin">
                  <Route path="notices" element={<ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN, UserRole.RECRUITMENT_ADMIN, UserRole.STUDENT_ADMIN]}><NoticeManagement /></ProtectedRoute>} />
                  <Route path="admission-vacancies" element={<ProtectedRoute allowedRoles={[UserRole.STUDENT_ADMIN]}><AdmissionVacancyManagement /></ProtectedRoute>} />
                  <Route path="master-data" element={<ProtectedRoute allowedRoles={[UserRole.RECRUITMENT_ADMIN]}><MasterDataManagement /></ProtectedRoute>} />
                  <Route path="users" element={<ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}><UserManagement /></ProtectedRoute>} />
                </Route>

                {/* Quarters Module Routes */}
                <Route path="/quarters">
                  <Route index element={<ProtectedRoute disallowedRoles={[UserRole.APPLICANT]}><QuartersDashboard /></ProtectedRoute>} />
                  <Route path="apply" element={<ProtectedRoute disallowedRoles={[UserRole.APPLICANT]}><ApplicationForm /></ProtectedRoute>} />
                  <Route path="apply/:applicationId" element={<ProtectedRoute disallowedRoles={[UserRole.APPLICANT]}><ApplicationForm /></ProtectedRoute>} />
                  <Route path="my-applications" element={<ProtectedRoute disallowedRoles={[UserRole.APPLICANT]}><ApplicationList /></ProtectedRoute>} />
                  <Route path="application/:id" element={<ProtectedRoute disallowedRoles={[UserRole.APPLICANT]}><ApplicationDetail /></ProtectedRoute>} />
                  <Route path="inventory" element={<ProtectedRoute disallowedRoles={[UserRole.APPLICANT]}><QuartersInventory /></ProtectedRoute>} />
                  <Route path="flat/:flatId" element={<ProtectedRoute disallowedRoles={[UserRole.APPLICANT]}><FlatDetail /></ProtectedRoute>} />

                  {/* Privileged Admin Routes */}
                  <Route path="admin/buildings" element={<ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}><BuildingManagement /></ProtectedRoute>} />
                  <Route path="admin/flats" element={<ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}><FlatManagement /></ProtectedRoute>} />
                  <Route path="admin/applications" element={<ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}><ApplicationList /></ProtectedRoute>} />
                  <Route path="admin/allotment-engine" element={<ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}><AllotmentEngine /></ProtectedRoute>} />
                  <Route path="admin/rules" element={<ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}><WeightSettings /></ProtectedRoute>} />
                  <Route path="admin/reservations" element={<ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}><ReservationSettings /></ProtectedRoute>} />
                  <Route path="admin/reports" element={<ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}><Reports /></ProtectedRoute>} />
                  <Route path="admin/audit-logs" element={<ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}><AuditLogs /></ProtectedRoute>} />
                </Route>

                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </HeaderFooterLayout>
          }
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
